import React, { useState, useEffect } from "react";
import { XMarkIcon, ArchiveBoxIcon, TruckIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import ModernStorageSelector from "../../utils/Selector/ModernStorageSelector";

/**
 * Item Handling Dialog Component
 *
 * Allows users to select storage and quantity when handling items
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Object} props.itemListItem - The item list item being handled
 * @param {string} props.action - 'return_to_storage', 'move_to_vehicle' or 'move_to_storage'
 * @param {Function} props.onConfirm - Callback when user confirms handling
 * @param {Function} props.onCancel - Callback when user cancels
 */
export default function ItemHandlingDialog({
  open = false,
  itemListItem,
  action,
  onConfirm,
  onCancel,
  busy = false,
  serverError = "",
  needsReload = false,
  onReload,
}) {
  const [quantity, setQuantity] = useState("");
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [showStorageSelector, setShowStorageSelector] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset state when dialog opens
  useEffect(() => {
    if (open && itemListItem) {
      const maxQuantity = itemListItem.quantity || 0;
      setQuantity(maxQuantity.toString());
      setSelectedStorage(null);
      setErrors({});
    }
  }, [open, itemListItem]);

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    const numValue = parseFloat(value);

    // Allow empty string or valid numbers
    if (value === "" || (!isNaN(numValue) && numValue >= 0)) {
      setQuantity(value);
      setErrors(prev => ({ ...prev, quantity: null }));
    }
  };

  const handleStorageSelect = (storage) => {
    setSelectedStorage(storage);
    setShowStorageSelector(false);
    setErrors(prev => ({ ...prev, storage: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    const numQuantity = parseFloat(quantity);
    const maxQuantity = itemListItem?.quantity || 0;

    if (!quantity || isNaN(numQuantity) || numQuantity <= 0) {
      newErrors.quantity = "Antall må være større enn 0";
    } else if (!/^\d+(?:\.\d{1,2})?$/.test(quantity)) {
      newErrors.quantity = "Bruk høyst to desimaler";
    } else if (numQuantity > maxQuantity) {
      newErrors.quantity = `Antall kan ikke være større enn ${maxQuantity}`;
    }

    if (!selectedStorage) {
      newErrors.storage = action === 'move_to_vehicle' ? "Du må velge et kjøretøy" : "Du må velge et lager";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (busy || needsReload) return;
    if (!validateForm()) return;

    const numQuantity = parseFloat(quantity);
    const maxQuantity = itemListItem?.quantity || 0;

    // If partial quantity, we'll need to handle the remainder
    const remainingQuantity = maxQuantity - numQuantity;

    onConfirm({
      action: action,
      quantity: numQuantity,
      remainingQuantity: remainingQuantity,
      storageId: selectedStorage?.id || null,
      storage: selectedStorage,
    });
  };

  const getDialogTitle = () => {
    if (action === 'return_to_storage') {
      return 'Returner til lager';
    } else if (action === 'move_to_vehicle') {
      return 'Flytt til kjøretøy';
    } else if (action === 'move_to_storage') {
      return 'Flytt til annet lager';
    }
    return 'Håndter vare';
  };

  const getActionIcon = () => {
    if (action === 'return_to_storage') {
      return <ArchiveBoxIcon className="h-6 w-6 text-green-600" />;
    } else if (action === 'move_to_vehicle') {
      return <TruckIcon className="h-6 w-6 text-blue-600" />;
    } else if (action === 'move_to_storage') {
      return <ArchiveBoxIcon className="h-6 w-6 text-indigo-600" />;
    }
    return null;
  };

  const getActionColor = () => {
    if (action === 'return_to_storage') {
      return 'bg-green-600 hover:bg-green-700';
    } else if (action === 'move_to_vehicle') {
      return 'bg-blue-600 hover:bg-blue-700';
    } else if (action === 'move_to_storage') {
      return 'bg-indigo-600 hover:bg-indigo-700';
    }
    return 'bg-gray-600 hover:bg-gray-700';
  };

  if (!open || !itemListItem) return null;

  const maxQuantity = itemListItem.quantity || 0;
  const numQuantity = parseFloat(quantity) || 0;
  const willHaveRemainder = numQuantity < maxQuantity && numQuantity > 0;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {getActionIcon()}
              <h3 className="text-lg font-semibold text-gray-900">
                {getDialogTitle()}
              </h3>
            </div>
            <button
              onClick={onCancel}
              disabled={busy || needsReload}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {serverError && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <p>{serverError}</p>
                {needsReload && <button type="button" onClick={onReload} className="mt-2 font-semibold underline">Hent siste status</button>}
              </div>
            )}
            {/* Item Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {itemListItem.order_item?.product?.displayTitle ||
                     itemListItem.order_item?.product?.title ||
                     itemListItem.order_item?.title ||
                     `Produkt ${itemListItem.order_item?.product_id || "Ukjent"}`}
                  </h4>
                  {itemListItem.order_item?.product?.item_number && (
                    <p className="text-sm text-gray-600">
                      Art.nr: {itemListItem.order_item.product.item_number}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">
                    Tilgjengelig antall: {maxQuantity}
                  </p>
                </div>
              </div>
            </div>

            {/* Quantity Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Antall å håndtere
              </label>
              <input
                type="number"
                value={quantity}
                onChange={handleQuantityChange}
                min="0"
                max={maxQuantity}
                step="0.01"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.quantity ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0"
              />
              {errors.quantity && (
                <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
              )}
            </div>

            {/* Storage Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {action === 'move_to_vehicle' ? 'Velg kjøretøy-lager' : 'Velg lager'}
              </label>
              {selectedStorage ? (
                <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedStorage.storage_number ? `${selectedStorage.storage_number} - ` : ""}
                      {selectedStorage.title || `Lager ${selectedStorage.id}`}
                    </p>
                    {selectedStorage.vehicle && (
                      <p className="text-sm text-gray-600">
                        Kjøretøy: {selectedStorage.vehicle.title || selectedStorage.vehicle.name}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowStorageSelector(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Endre
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowStorageSelector(true)}
                  className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                >
                  {action === 'move_to_vehicle' ? (
                    <>
                      <TruckIcon className="h-5 w-5 mr-2" />
                      Velg kjøretøy-lager
                    </>
                  ) : (
                    <>
                      <ArchiveBoxIcon className="h-5 w-5 mr-2" />
                      Velg lager
                    </>
                  )}
                </button>
              )}
              {errors.storage && (
                <p className="mt-1 text-sm text-red-600">{errors.storage}</p>
              )}
            </div>

            {/* Return to Storage Info */}
            {action === 'return_to_storage' && !selectedStorage && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <ArchiveBoxIcon className="h-5 w-5 text-blue-600" />
                  <p className="text-sm text-blue-800">
                    Velg lager hvor varen skal returneres
                  </p>
                </div>
              </div>
            )}

            {/* Remainder Info */}
            {willHaveRemainder && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Restantall: {(maxQuantity - numQuantity).toFixed(2)}
                    </p>
                    <p className="text-sm text-yellow-700">
                      Resten legges til for videre håndtering i samme liste
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              onClick={onCancel}
              disabled={busy || needsReload}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Avbryt
            </button>
            <button
              onClick={handleConfirm}
              disabled={busy || needsReload}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${getActionColor()}`}
            >
              Bekreft
            </button>
          </div>
        </div>
      </div>

      {/* Storage Selector Modal */}
      {showStorageSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {action === 'move_to_vehicle' ? 'Velg kjøretøy-lager' : 'Velg lager'}
                </h3>
                <button
                  onClick={() => setShowStorageSelector(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <ModernStorageSelector
                open={true}
                selectedStorage={selectedStorage}
                onChange={handleStorageSelect}
                onClose={() => setShowStorageSelector(false)}
                multiple={false}
                showHeader={false}
                filterVehicle={action === 'move_to_vehicle'}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
